import { getDataExportErrorMessages } from 'common/queries/dataExport';
import { ToastContent } from 'common/types/notification';

// en-GB gives a day-month-year order that reads unambiguously ("5 Oct 2026"), always with the year.
const LOCALE = 'en-GB';

export const WEBHOOK_SECRET_MIN_LENGTH = 16;
export const WEBHOOK_URL_MAX_LENGTH = 2000;
export const TOKEN_NAME_MAX_LENGTH = 100;

/** e.g. "5 Oct 2026" in the viewer's time zone. */
/** An `aria-describedby` value from the ids that apply (hint, error), or undefined when none does. */
export const describedBy = (...ids: (string | false | undefined | null)[]): string | undefined =>
  ids.filter(Boolean).join(' ') || undefined;

export const formatExportDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' });

/** e.g. "09:30" (24-hour) in the viewer's time zone. */
export const formatExportTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', hour12: false });

/** e.g. "5 Oct 2026, 09:30" (24-hour) in the viewer's time zone. */
export const formatExportDateTime = (iso: string): string =>
  new Date(iso).toLocaleString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/** Splits text on backticks so `like this` renders as inline code; the translations use it for API names. */
export const splitInlineCode = (text: string): { text: string; isCode: boolean }[] =>
  text
    .split('`')
    .map((part, index) => ({ text: part, isCode: index % 2 === 1 }))
    .filter(({ text }) => text.length > 0);

/** The backend's messages when it sent any (422), otherwise the fallback. */
export const getErrorText = (error: unknown, fallback: string): string => {
  const messages = getDataExportErrorMessages(error);
  return messages.length ? messages.join(' ') : fallback;
};

export const errorToast = (error: unknown, fallback: string): ToastContent => ({ status: 'error', message: getErrorText(error, fallback) });

export interface WebhookFormErrors {
  url?: 'urlRequired' | 'urlHttps' | 'urlTooLong';
  secret?: 'secretTooShort';
}

/** Client-side checks only; the backend also rejects non-public URLs, credentials, fragments and other ports, and enforces the limit. */
export const validateWebhookForm = ({ url, secret }: { url: string; secret: string }): WebhookFormErrors => {
  const errors: WebhookFormErrors = {};
  const trimmedUrl = url.trim();

  if (!trimmedUrl) errors.url = 'urlRequired';
  else if (!trimmedUrl.toLowerCase().startsWith('https://') || trimmedUrl.length === 'https://'.length) errors.url = 'urlHttps';
  else if (trimmedUrl.length > WEBHOOK_URL_MAX_LENGTH) errors.url = 'urlTooLong';

  if (secret.length > 0 && secret.length < WEBHOOK_SECRET_MIN_LENGTH) errors.secret = 'secretTooShort';

  return errors;
};

/** Token names are optional; the backend trims them and rejects more than 100 characters. */
export const isTokenNameTooLong = (name: string): boolean => name.trim().length > TOKEN_NAME_MAX_LENGTH;

/** How a token is referred to in confirmations: its name and hint, or just the hint when unnamed. */
export const tokenLabel = ({ name, hint }: { name: string | null; hint: string }): string => (name ? `${name} (…${hint})` : `…${hint}`);
